import { GridColDef } from "@mui/x-data-grid";
import { Typography, Box, Paper, Stack } from "@mui/material";
import CustomDataGrid from "../../customDataGrid/CustomDataGrid";
import { useMemo } from "react";
import AssessmentIcon from '@mui/icons-material/Assessment';

const ForecastTotalData = ({ monthList, totalBySection }: { monthList: any, totalBySection: any }) => {

    // 1. Définition des colonnes
    const columns: GridColDef[] = useMemo(() => [
        {
            field: "section",
            headerName: "Section",
            minWidth: 250,
            flex: 1,
            renderCell: (params: any) => (
                <Typography variant="body2" sx={{ fontWeight: params.row.id === 'total' ? 900 : 600 }}>
                    {params.value}
                </Typography>
            ),
        },
        ...monthList.map((m: any) => ({
            field: m.field,
            headerName: m.name,
            minWidth: 150,
            align: "right" as const,
            headerAlign: "right" as const,
            renderCell: (params: any) => (
                <Typography 
                    variant="body2" 
                    sx={{ 
                        fontWeight: params.row.id === 'total' ? 800 : 500,
                        color: params.row.isCharge && params.row.id !== 'total' ? '#ef4444' : 'inherit'
                    }}
                >
                    {params.value}
                </Typography>
            )
        }))
    ], [monthList]);

    // 2. Définition des lignes (C'est ici que 'rows' est créé)
    const rows = useMemo(() => {
        const sectionsKeys = Object.keys(totalBySection).filter(k => k !== "TOTAL");
        
        // Calcul du Résultat Net
        const resultNetValues = monthList.reduce((acc: any, month: any) => {
            const m = month.field;
            const ca = (totalBySection["CA - Régie"]?.[m] || 0) +
                       (totalBySection["CA - Forfait"]?.[m] || 0) +
                       (totalBySection["CA - Hébergement"]?.[m] || 0) +
                       (totalBySection["CA - TMA/TME"]?.[m] || 0) +
                       (totalBySection["CA - TRA"]?.[m] || 0);

            const charges = (totalBySection["Salariés"]?.[m] || 0) +
                            (totalBySection["TNS"]?.[m] || 0) +
                            (totalBySection["Frais de fonctionnement"]?.[m] || 0) +
                            (totalBySection["Investissements"]?.[m] || 0) +
                            (totalBySection["Entreprise"]?.[m] || 0) +
                            (totalBySection["Sous-traitance"]?.[m] || 0) +
                            (totalBySection["DGFIP"]?.[m] || 0);

            acc[m] = ca - charges;
            return acc;
        }, {});

        const dataRows = sectionsKeys.map((key, index) => {
            const isCharge = !key.includes("CA -");
            const row: any = { id: index, section: key, isCharge };

            monthList.forEach((m: any) => {
                const val = totalBySection[key]?.[m.field] || 0;
                row[m.field] = `${isCharge && val !== 0 ? '-' : ''}${Number(val).toLocaleString(undefined, { minimumFractionDigits: 2, maximumFractionDigits: 2 })} €`;
            });
            return row;
        });

        const totalRow = {
            id: "total",
            section: "RÉSULTAT NET (EBITDA)",
            isCharge: false,
            ...Object.fromEntries(
                Object.entries(resultNetValues).map(([m, val]) => [
                    m, `${Number(val as number).toLocaleString(undefined, { minimumFractionDigits: 2, maximumFractionDigits: 2 })} €`
                ])
            )
        };

        return [...dataRows, totalRow];
    }, [totalBySection, monthList]);

    // 3. Rendu du composant
    return (
        <Box sx={{ mt: 4, mb: 8 }}>
            <Paper sx={{ p: 3, borderRadius: 4, border: "1px solid #e2e8f0", boxShadow: "0px 4px 20px rgba(0,0,0,0.05)" }}>
                <Stack direction="row" spacing={2} alignItems="center" sx={{ mb: 3 }}>
                    <AssessmentIcon sx={{ color: "#6366f1" }} />
                    <Typography variant="h5" sx={{ fontWeight: 800, color: "#1e293b" }}>
                        Synthèse Globale
                    </Typography>
                </Stack>

                <CustomDataGrid
                    disableRowSelectionOnClick
                    rows={rows} // Maintenant 'rows' est bien défini au-dessus !
                    columns={columns}
                    getRowHeight={() => 'auto'}
                    sx={{
                        border: "none",
                        "& .MuiDataGrid-footerContainer": { display: "none" },
                        "& .MuiDataGrid-columnHeaders": { bgcolor: "#f8fafc" },
                        "& .MuiDataGrid-cell": { py: 1.5 },
                        "& .MuiDataGrid-row[data-id='total']": {
                            bgcolor: "#fef3c7 !important",
                            "& .MuiDataGrid-cell": {
                                color: "#b45309",
                                fontWeight: "900 !important",
                                borderTop: "2px solid #fcd34d"
                            }
                        }
                    }}
                />
            </Paper>
        </Box>
    );
}

export default ForecastTotalData;